import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { useCreateSASTokenMutation } from 'common/api/sasToken';
import { setSessionSASToken } from 'common/slice/SessionSlice';
import { handleError } from 'helpers/sentry';

const inflightSessions = new Set<string>();

const useSessionSasToken = (
  sessionId?: string | number,
  sessionUuid?: string,
  shouldFetch = true,
) => {
  const dispatch = useAppDispatch();
  const [createSasToken] = useCreateSASTokenMutation();
  const normalizedSessionId = sessionId?.toString();
  const sessionIdentifier = sessionId ?? normalizedSessionId;
  const token = useAppSelector((state) =>
    normalizedSessionId ? state.session.token[normalizedSessionId]?.value : undefined,
  );

  useEffect(() => {
    if (
      !shouldFetch ||
      !normalizedSessionId ||
      !sessionUuid ||
      !!token ||
      inflightSessions.has(normalizedSessionId)
    ) {
      return;
    }

    let isMounted = true;
    inflightSessions.add(normalizedSessionId);

    const fetchToken = async () => {
      try {
        const sasToken = await createSasToken({
          container_name: sessionUuid,
          session_id: sessionIdentifier,
        }).unwrap();

        if (isMounted && sasToken) {
          dispatch(
            setSessionSASToken({
              [normalizedSessionId]: {
                value: sasToken,
              },
            }),
          );
        }
      } catch (error) {
        if (isMounted) {
          handleError(error);
        }
      } finally {
        inflightSessions.delete(normalizedSessionId);
      }
    };

    fetchToken();

    return () => {
      isMounted = false;
    };
  }, [createSasToken, dispatch, normalizedSessionId, sessionIdentifier, sessionUuid, shouldFetch, token]);

  return token;
};

export default useSessionSasToken;
